import React from 'react';
import IngredientMergePicker from './IngredientMergePicker';
import { HEADS } from './notInRecipeApi';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const qty = (n, unit) => `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${unit || ''}`.trim();
const dayText = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${Number(m[3])} ${SHORT[Number(m[2]) - 1]}` : '';
};
const monthName = (ym) => MONTHS[Number(String(ym || '').slice(5, 7)) - 1] || '';
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** The card's one line of figures (spec §9.3). Before the count, use and money are estimates (≈). */
export function detailsLine(card, month) {
  const est = card.estimate ? '≈ ' : '';
  const bills = card.bills > 0 ? plural(card.bills, 'bill') : 'No bills';
  if (card.section === 'added_to_recipe') {
    return `Recipe added ${dayText(card.recipe_added_on)} · ${est}${money(card.amount)} before that is booked here`;
  }
  let parts;
  if (card.kind === 'bought') {
    parts = [bills, `${money(card.bought_value)} bought`];
  } else if (card.kind === 'counted' && card.is_non_consumable) {
    parts = [bills, `${money(card.bought_value)} bought`, `counted: ${est}${qty(card.closing_qty, card.unit)} left`, `${est}${money(card.amount)} used`];
  } else if (card.kind === 'counted') {
    parts = card.bills > 0
      ? [bills, `bought ${qty(card.bought_qty, card.unit)}`, `used ${est}${qty(card.used_qty, card.unit)}`, `${est}${money(card.amount)}`]
      : [bills, 'used from opening stock', `${est}${money(card.amount)}`];
  } else {
    parts = [bills, `bought ${qty(card.bought_qty, card.unit)}`, money(card.amount)];
  }
  if (card.head && card.remembered_from_month && card.remembered_from_month < month) {
    parts.push(`same as ${monthName(card.remembered_from_month)}`);
  }
  return parts.join(' · ');
}

const FLAG_TEXT = {
  COUNT_ABOVE_AVAILABLE: 'Counted more than available. Booked as ₹0. Check the count.',
  NOT_COUNTED: 'Not counted — the full purchase is booked. Include its category in the count for the real figure.',
};

/**
 * One item no dish uses (spec §9.3–§9.6): its figures, its flags, one expense head chosen with
 * a pill (a suggestion is dashed and is not an answer until tapped), "Count it" for a
 * non-consumable, a rate when no price exists anywhere, and Merge… for a duplicate name.
 */
export default function NotInRecipeCard({
  card, choice, month, busy, mergeOpen, mergeDisabledReason, mergeError, rateError, countItNote, ingredients,
  onChoose, onToggleMerge, onCountIt, onRate, onMerge, onAddToRecipe,
}) {
  const head = choice ? choice.head : null;
  const needsRate = card.flags.includes('NO_PRICE');
  return (
    <div className="um-card" data-item-id={card.item_id}>
      <div className="nir-card-head">
        <div className="um-name">{card.name}</div>
        {!card.is_non_consumable && card.section !== 'added_to_recipe' && onAddToRecipe && (
          <button type="button" className="nir-add-recipe" disabled={busy} onClick={onAddToRecipe}>Add to recipe →</button>
        )}
      </div>
      <div className="nir-tags">
        <span className={`nir-tag ${card.is_non_consumable ? 'nir-tag-nc' : 'nir-tag-consumable'}`}>
          {card.is_non_consumable ? 'Non-consumable' : 'Consumable'}
        </span>
        {card.tags.includes('new') && <span className="nir-tag nir-tag-new">New</span>}
        {card.tags.includes('bought_often') && <span className="nir-tag nir-tag-often">Bought often</span>}
      </div>
      <div className="um-sub">{detailsLine(card, month)}</div>

      {card.flags.filter((f) => FLAG_TEXT[f]).map((f) => (
        <div key={f} className="nir-flag" role="note">{FLAG_TEXT[f]}</div>
      ))}
      {needsRate && (
        <div className="nir-flag" role="note">
          No price found. Enter the rate per {card.unit}.
          <div className="nir-rate">
            <span>₹</span>
            <input type="number" min="0" step="any" inputMode="decimal" aria-label={`Rate per ${card.unit} for ${card.name}`}
              value={choice && choice.rate !== undefined ? choice.rate : ''} disabled={busy}
              onChange={(e) => onRate(e.target.value)} />
            <span>/ {card.unit}</span>
            {rateError && <span className="nir-rate-error">{rateError}</span>}
          </div>
        </div>
      )}

      <div className="nir-pills" role="group" aria-label={`Expense head for ${card.name}`}>
        {HEADS.map(([key, label]) => {
          const selected = head === key;
          const suggested = !head && card.suggested_head === key;
          return (
            <button key={key} type="button" aria-pressed={selected} disabled={busy}
              className={`nir-pill${selected ? ' nir-pill-selected' : ''}${suggested ? ' nir-pill-suggested' : ''}`}
              onClick={() => onChoose(key)}>
              {label}
            </button>
          );
        })}
        <button type="button" aria-pressed={mergeOpen} disabled={busy || Boolean(mergeDisabledReason)}
          title={mergeDisabledReason || undefined}
          className={`nir-pill${mergeOpen ? ' nir-pill-selected' : ''}`} onClick={onToggleMerge}>
          Merge…
        </button>
        {card.is_non_consumable && (
          <label className="nir-count-it">
            <input type="checkbox" checked={Boolean(choice && choice.count_at_close)} disabled={busy}
              onChange={(e) => onCountIt(e.target.checked)} />
            Count it
          </label>
        )}
      </div>
      {mergeDisabledReason && <div className="nir-note">{mergeDisabledReason}</div>}
      {card.is_non_consumable && choice && choice.count_at_close && countItNote && <div className="nir-note">{countItNote}</div>}
      {mergeError && <div className="um-error" role="alert" style={{ marginTop: 8 }}>{mergeError}</div>}
      {mergeOpen && !mergeDisabledReason && (
        <IngredientMergePicker card={card} ingredients={ingredients} busy={busy}
          onConfirm={onMerge} onCancel={onToggleMerge} />
      )}
    </div>
  );
}
